const DEFAULTS = {
  triggerUrl: "#monogram-placement",
};

/**
 * Hands the markup held in its <template> to a markup-modal whenever a link
 * anywhere on the page points at the configured anchor.
 *
 * <monogram-placement-trigger modal-target="monogram-placement-123" trigger-url="#monogram-placement">
 *   <template data-content>…</template>
 * </monogram-placement-trigger>
 */
class MonogramPlacementTrigger extends HTMLElement {
  connectedCallback() {
    this.template = this.querySelector("template[data-content]");
    if (!this.template) return;

    this.handleClick = (e) => {
      const link = e.target.closest("a[href]");
      if (!link || !this._matches(link)) return;

      e.preventDefault();
      this._open(link);
    };

    // The modal is unreachable in the theme editor otherwise — there is nothing
    // on the page to click, and the section itself renders no visible markup.
    this.handleSelect = (e) => {
      if (!e.target.contains(this)) return;

      this._open(this);
    };

    this.handleDeselect = (e) => {
      if (!e.target.contains(this)) return;

      document.dispatchEvent(new CustomEvent("markup-modal:close"));
    };

    document.addEventListener("click", this.handleClick);
    document.addEventListener("shopify:section:select", this.handleSelect);
    document.addEventListener("shopify:section:deselect", this.handleDeselect);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleClick);
    document.removeEventListener("shopify:section:select", this.handleSelect);
    document.removeEventListener(
      "shopify:section:deselect",
      this.handleDeselect,
    );
  }

  _matches(link) {
    const trigger = this.triggerUrl;

    // Anchors are matched against the current page, so a link carrying the same
    // hash but pointing elsewhere still navigates rather than opening the modal.
    if (trigger.startsWith("#")) {
      return (
        link.hash === trigger && link.pathname === window.location.pathname
      );
    }

    return link.href === new URL(trigger, window.location.href).href;
  }

  _open(opener) {
    opener.dispatchEvent(
      new CustomEvent("markup-modal:open", {
        bubbles: true,
        detail: {
          target: this.modalTarget,
          html: this.template.innerHTML,
        },
      }),
    );
  }

  get triggerUrl() {
    return this.getAttribute("trigger-url") || DEFAULTS.triggerUrl;
  }

  get modalTarget() {
    return this.getAttribute("modal-target");
  }
}

/**
 * Drives the surrounding tab-component from a <select>, so the same panels can
 * be reached from the mobile layout where the tab list itself is hidden. Each
 * option's value is the id of the tab it stands for.
 */
class MonogramPlacementSelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector("select");
    this.tabComponent = this.closest("tab-component");
    if (!this.select || !this.tabComponent) return;

    this.handleChange = () => {
      const tab = this.tabComponent.querySelector(`#${this.select.value}`);
      if (!tab) return;

      // The tab list is display:none at this breakpoint, so moving focus onto
      // the tab would drop it off the page entirely.
      this.tabComponent.setSelectedTab(tab, false);
    };

    this.handleTabSelected = (e) => {
      this.select.value = e.detail.tab.id;
    };

    this.select.addEventListener("change", this.handleChange);
    this.tabComponent.addEventListener(
      "tab-component:tab-selected",
      this.handleTabSelected,
    );
  }

  disconnectedCallback() {
    this.select?.removeEventListener("change", this.handleChange);
    this.tabComponent?.removeEventListener(
      "tab-component:tab-selected",
      this.handleTabSelected,
    );
  }
}

if (!customElements.get("monogram-placement-trigger")) {
  customElements.define("monogram-placement-trigger", MonogramPlacementTrigger);
}

if (!customElements.get("monogram-placement-select")) {
  customElements.define("monogram-placement-select", MonogramPlacementSelect);
}
